import { useCallback, useContext, useEffect, useState } from 'react';
import { IApproveFinanceData } from '../model/IApproveFinanceData';
import { FinanceQuoteContext } from './FinanceQuoteContext';
import CalculatorEvents from '../constants/calculatorEvents';
import financeQuoteTransformer from '../service/financeQuoteTransformer';
import { IFinanceCalculatorConfiguratorWidget } from '../components/financeCalculator/FinanceCalculatorConfiguratorWidget';
import dealApi from '../../common/api/dealApi';
import { ICarDetails } from '../../../../common/model/ICarDetails';
import { MultipleQuotesContext } from './MultipleQuotesContext';
import partExchangeTransformer from '../../common/service/partExchangeTrasformer';
import { PricesContext } from '../../common/react/PricesContext';
import numberUtilities from '../../../../common/utilities/numberUtilities';
import { AxiosError } from 'axios';

interface IUseFinCalcConfiguratorPayload {
  postInitialization?: (widgetInstance: any, widgetId: string) => void;
  isDealLoading: boolean;
  financeError: Error;
}

type TUseFinCalcConfigurator = (props: IFinanceCalculatorConfiguratorWidget) => IUseFinCalcConfiguratorPayload;

export const useFinCalcConfigurator: TUseFinCalcConfigurator = ({ toggleModal, carConfiguration }) => {
  const [isDealLoading, setIsDealLoading] = useState(true);
  const [financeError, setFinanceError] = useState<Error>(null);
  const { setFinanceQuote, setSecondaryFinanceQuote } = useContext(FinanceQuoteContext);
  const { setFinanceQuotes } = useContext(MultipleQuotesContext);
  const { setPartExchange, setDeposit } = useContext(PricesContext);

  const destroyFinCalcWidget = useCallback(widgetId => {
    toggleModal(false);
    (window as any).FC.InchcapeFinance.destroy(`#${widgetId}`);
  }, []);

  useEffect(() => {
    dealApi
      .getForCar(carConfiguration as ICarDetails)
      .catch((error: AxiosError) => setFinanceError(error))
      .finally(() => setIsDealLoading(false));
  }, []);

  const postInitialization = useCallback((widgetInstance, widgetId) => {
    widgetInstance.on(CalculatorEvents.SAVE, (plainQuote: IApproveFinanceData) => {
      destroyFinCalcWidget(widgetId);
      setFinanceQuote(financeQuoteTransformer.from(plainQuote.activeDeal.financeQuote));

      if (plainQuote.additionalVariantsQuotes) {
        setFinanceQuotes(plainQuote.additionalVariantsQuotes);
      }
    });

    widgetInstance.on(CalculatorEvents.CONTINUE, (plainQuote: IApproveFinanceData) => {
      destroyFinCalcWidget(widgetId);
      setFinanceQuote(financeQuoteTransformer.from(plainQuote.activeDeal.financeQuote));

      if (plainQuote.activeDeal.financeQuote?.secondaryCurrencyQuote) {
        setSecondaryFinanceQuote(
          financeQuoteTransformer.from(plainQuote.activeDeal.financeQuote.secondaryCurrencyQuote),
        );
      }

      if (
        plainQuote.activeDeal?.partExchange &&
        numberUtilities.isNumber(partExchangeTransformer.extractValuationPrice(plainQuote.activeDeal?.partExchange))
      ) {
        setPartExchange(partExchangeTransformer.extractValuationPrice(plainQuote.activeDeal?.partExchange));
      } else {
        setPartExchange(null);
      }

      if (plainQuote.activeDeal?.financeQuote?.deposit) {
        setDeposit(plainQuote.activeDeal.financeQuote.deposit);
      } else {
        setDeposit(0);
      }

      if (plainQuote.additionalVariantsQuotes) {
        setFinanceQuotes(plainQuote.additionalVariantsQuotes);
      }
      if (plainQuote.activeDeal.financeQuote?.financeProductKey === 'CASH') {
        setFinanceQuotes(null);
      }
    });
  }, []);

  return { postInitialization, isDealLoading, financeError };
};
